import { ChunkLoadRecovery } from './chunk-load-recovery.token';

export const RELOAD_MARKER_KEY = 'ufp-i18n-chunk-reload';

/** In-memory single-flight guard — prevents concurrent reload races in the same tab. */
let reloadInitiated = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: unknown }).message ?? '')
          : '';

  const name =
    error instanceof Error
      ? error.name
      : typeof error === 'object' && error !== null && 'name' in error
        ? String((error as { name?: unknown }).name ?? '')
        : '';

  return (
    name === 'ChunkLoadError' ||
    /loading chunk [\w-]+ failed/i.test(message) ||
    /chunkloaderror/i.test(message)
  );
}

function readReloadMarker(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_MARKER_KEY);
  } catch {
    return null;
  }
}

function writeReloadMarker(): void {
  try {
    sessionStorage.setItem(RELOAD_MARKER_KEY, '1');
  } catch {
    // Storage may be blocked; in-memory guard still limits reload attempts.
  }
}

function removeReloadMarker(): void {
  try {
    sessionStorage.removeItem(RELOAD_MARKER_KEY);
  } catch {
    // Ignore storage access failures.
  }
}

export function createBrowserChunkLoadRecovery(
  reload: () => void = () => window.location.reload(),
): ChunkLoadRecovery {
  return {
    clearReloadMarker(): void {
      removeReloadMarker();
    },

    handleChunkLoadFailure(): void {
      if (typeof window === 'undefined') {
        return;
      }

      // Synchronous in-memory guard: only one reload per page lifetime.
      if (reloadInitiated) {
        return;
      }

      const markerAlreadySet = readReloadMarker() !== null;
      if (markerAlreadySet) {
        return;
      }

      reloadInitiated = true;
      writeReloadMarker();
      reload();
    },
  };
}

/** Resets module state between unit tests only. */
export function resetChunkLoadRecoveryStateForTesting(): void {
  reloadInitiated = false;
  removeReloadMarker();
}
